import { useCallback, useRef, useState, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useDurationBetweenTwoPoints } from 'tripPlanner/hooks/api/getDurationBetweenTwoPoints'
import { useUpdateTravelPreferences } from 'tripPlanner/hooks/api/useUpdateTravelPreferences'
import type { TripItem } from 'tripPlanner/types/tripItem'
import type moment from 'moment'
import { getItemPlace } from 'tripPlanner/utils'
import Clickable from 'components/Common/Clickable'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import useToggle from 'hooks/useToggle'
import { formatMinutesToHoursMinutes, secondsToMinutes } from 'lib/datetime/time'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineWalkingIcon from 'LuxKit/Icons/line/LineWalkingIcon'
import SolidCarIcon from 'LuxKit/Icons/solid/SolidCarIcon'
import { getDestinationPlace, isFirstItemOfDay } from './durationUtils'
import type { TravelMode, RouteSummary } from 'tripPlanner/api/routes/getRouteInfo'
import { DISTANCE_THRESHOLD_BETWEEN_WALK_AND_DRIVE } from 'tripPlanner/api/routes/getRouteInfo'
import TravelModeList from './TravelModeList'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCurrentSelectionId } from 'tripPlanner/reducers/actions'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'

const IconContainer = styled(Group)`
  z-index: 1;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.round};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  width: ${rem(20)};
  height: ${rem(20)};
  ${setSvgIconSize(10)};

  @container (min-width: 640px) {
    width: ${rem(24)};
    height: ${rem(24)};
    ${setSvgIconSize(12)};
  }
`

export type TravelData = Pick<RouteSummary, 'duration' | 'distance' | 'travelMode'>

interface Props {
  fromItem: TripItem
  toItem: TripItem
  allDayItems?: Array<TripItem>
  currentDate?: moment.Moment
  fromHotel?: boolean
  toHotel?: boolean
}

export function getTravelModeIcon(travelMode: TravelMode, colour?: App.Design.ContentColour) {
  switch (travelMode) {
    case 'DRIVE':
      return <IconContainer className="pin-anchor" direction="horizontal" verticalAlign="center" horizontalAlign="center">
        <SolidCarIcon colour={colour} />
      </IconContainer>
    default:
      return <IconContainer className="pin-anchor" direction="horizontal" verticalAlign="center" horizontalAlign="center">
        <LineWalkingIcon colour={colour} />
      </IconContainer>
  }
}

const getDropdownIcon = (isOpen: boolean) => isOpen ? <LineAngleUpIcon size="S" colour="highlight-secondary" /> : <LineAngleDownIcon size="S" colour="highlight-secondary" />

function TravelDurationIndicator({ fromItem, toItem, allDayItems, currentDate, fromHotel, toHotel }: Props) {
  const intl = useIntl()
  const { value: isDropdownOpen, toggle: toggleDropdown, off: closeDropdown } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [selectedTravelMode, setSelectedTravelMode] = useState<TravelMode | null>(null)
  const dispatch = useAppDispatch()

  const isFromItemAccommodation = fromItem.type === 'ACCOMMODATION'
  const isToItemFirstItem = isFirstItemOfDay(allDayItems, currentDate, toItem)

  const itemToUpdate = isFromItemAccommodation ? toItem : fromItem
  const currentTravelPreference = useMemo(() => {
    if (isFromItemAccommodation) return toItem.fromHotelTravelPreferences
    if (isToItemFirstItem) return toItem.travelPreferences
    return fromItem.travelPreferences
  }, [isFromItemAccommodation, isToItemFirstItem, toItem.fromHotelTravelPreferences, toItem.travelPreferences, fromItem.travelPreferences])

  const updateTravelPreferences = useUpdateTravelPreferences(
    itemToUpdate.id,
    itemToUpdate.tripId,
  )

  const handleTravelModeSelect = useCallback((mode: TravelMode) => {
    setSelectedTravelMode(mode)

    if (isFromItemAccommodation) {
      if (mode !== toItem.fromHotelTravelPreferences) {
        updateTravelPreferences.mutate({
          fromHotelTravelPreference: mode,
        })
      }
    } else {
      if (mode !== fromItem.travelPreferences) {
        updateTravelPreferences.mutate({
          travelPreference: mode,
        })
      }
    }

    dispatch(setCurrentSelectionId(tripItemSelectionId(toItem.id)))
    closeDropdown()
  }, [closeDropdown, fromItem.travelPreferences, toItem.fromHotelTravelPreferences, isFromItemAccommodation, updateTravelPreferences, dispatch, toItem.id])

  const destinationPlace = getDestinationPlace(fromItem, toItem, allDayItems, currentDate)
  const fromPlace = getItemPlace(fromItem, 'end')
  const fromGeoPlace = fromPlace?.type === 'GEO' ? fromPlace : undefined

  const requestedMode: TravelMode | undefined =
    currentTravelPreference === 'DRIVE' || currentTravelPreference === 'WALK' ? currentTravelPreference : undefined

  const { data, error } = useDurationBetweenTwoPoints(
    fromGeoPlace?.point,
    destinationPlace?.point,
    requestedMode,
  )

  const predictedPrimaryMode: TravelMode = useMemo(() => {
    if (fromGeoPlace?.point && destinationPlace?.point) {
      const km = calculateDistance(fromGeoPlace.point.lat, fromGeoPlace.point.lng, destinationPlace.point.lat, destinationPlace.point.lng)
      return km > DISTANCE_THRESHOLD_BETWEEN_WALK_AND_DRIVE ? 'DRIVE' : 'WALK'
    }
    return 'WALK'
  }, [fromGeoPlace?.point, destinationPlace?.point])

  const remainingModes: Array<TravelMode> = useMemo(() => {
    const baseMode = data?.travelMode ?? predictedPrimaryMode
    return baseMode === 'DRIVE' ? ['WALK'] : ['DRIVE']
  }, [data?.travelMode, predictedPrimaryMode])

  const altOptions = useMemo(() => ({ enabled: isDropdownOpen }), [isDropdownOpen])

  const { data: alternativeData, error: alternativeError } = useDurationBetweenTwoPoints(
    fromGeoPlace?.point,
    destinationPlace?.point,
    remainingModes[0],
    altOptions,
  )

  const getDataForTravelMode = useMemo(() => (travelMode: TravelMode) => {
    if (data?.travelMode === travelMode) {
      return data
    }
    if (alternativeData?.travelMode === travelMode) {
      return alternativeData
    }
    return null
  }, [data, alternativeData])

  const getErrorForTravelMode = useMemo(() => (travelMode: TravelMode) => {
    if (data?.travelMode === travelMode) {
      return error
    }
    if (alternativeData?.travelMode === travelMode) {
      return alternativeError
    }
    return null
  }, [data?.travelMode, alternativeData?.travelMode, error, alternativeError])

  const currentData = selectedTravelMode ? getDataForTravelMode(selectedTravelMode) : data
  const currentError = selectedTravelMode ? getErrorForTravelMode(selectedTravelMode) : error

  if (!fromGeoPlace || !destinationPlace || currentError || !currentData) {
    const unavailableIcon = getTravelModeIcon(predictedPrimaryMode)
    return (
      <Group direction="horizontal" gap={12} verticalAlign="center">
        {unavailableIcon}
        <Caption variant="medium">
          {intl.formatMessage({
            id: 'Q8PmRPGmJu-steQDV2Yu',
            defaultMessage: 'Travel info unavailable',
            description: 'Label shown when travel information between trip items is not available',
          })}
        </Caption>
      </Group>
    )
  }

  const durationInMinutes = currentData?.duration ? secondsToMinutes(parseInt(currentData.duration.replace('s', ''))) : 0
  const formattedDuration = formatMinutesToHoursMinutes(durationInMinutes, intl)
  const formattedDistance = `${Math.round(currentData.distance * 10) / 10} km`
  const travelModeIcon = getTravelModeIcon(currentData.travelMode)
  const dropdownIcon = getDropdownIcon(isDropdownOpen)
  const travelModeLabel = currentData.travelMode === 'DRIVE'
    ? intl.formatMessage({
        id: 'ae0sc8HOG2wR1QIqS-vq',
        defaultMessage: 'Drive',
        description: 'Label for the driving travel mode option in the trip planner directions display',
      })
    : intl.formatMessage({
        id: '1zc2Kb2Pg4Cb0mR3sW6Z',
        defaultMessage: 'Walk',
        description: 'Label for the walking travel mode option in the trip planner directions display',
      })
  const travelLabel = currentData?.duration
    ? toHotel
      ? intl.formatMessage(
          {
            id: 'THhhQkMVoUPikzbWqark',
            defaultMessage: '{travelMode} {duration} · {distance} to hotel',
            description: 'Travel duration info with hotel destination in the trip planner timeline view',
          },
          { travelMode: travelModeLabel, duration: formattedDuration, distance: formattedDistance },
        )
      : intl.formatMessage(
          {
            id: 'RZvcXZDc8aEcgRg_gcBt',
            defaultMessage: '{travelMode} {duration} · {distance}',
            description: 'Travel duration info between locations in the trip planner timeline view',
          },
          { travelMode: travelModeLabel, duration: formattedDuration, distance: formattedDistance },
        )
    : intl.formatMessage({
        id: '3c_BgmBySDm8-hGBgux6',
        defaultMessage: 'Unavailable',
        description: 'Label shown when travel duration data is not available',
      })
  if (currentData?.duration === '0s') {
    return null
  }
  const travelModes: Array<TravelData | null> = [
    data || null,
    alternativeData || null,
  ]

  return (
    <>
      <Group direction="horizontal" gap={12} verticalAlign="center">
        {travelModeIcon}
        <Clickable ref={triggerRef} onClick={toggleDropdown}>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <Caption variant="medium">{travelLabel}</Caption>
            {dropdownIcon}
          </Group>
        </Clickable>
      </Group>
      <DropdownList
        size="M"
        open={isDropdownOpen}
        onClose={closeDropdown}
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        portaled
        placement="bottom-start"
      >
        <TravelModeList
          travelDataArray={travelModes}
          onTravelModeSelect={handleTravelModeSelect}
          selectedTravelMode={selectedTravelMode}
          currentTravelPreference={currentTravelPreference}
          toHotel={toHotel}
          fromHotel={fromHotel}
        />
      </DropdownList>
    </>
  )
}

export default TravelDurationIndicator
